import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Adicionamos a prop 'theme' com valor padrão 'dark'
const Navbar = ({ theme = "dark" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Verifica se o tema passado é o claro
  const isLight = theme === "light";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'SOBRE NÓS', path: '/sobre' },
    { name: 'PRODUTOS', path: '/produtos' },
    { name: 'CONTATO', path: '/contato' },
  ];

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== href) {
      navigate(href);
      window.scrollTo(0, 0);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
          menuOpen
            ? "bg-usifresa-dark py-2" // Menu aberto: sempre escuro
            : scrolled
              ? isLight 
                ? "bg-white/95 backdrop-blur-md border-b border-gray-200 py-2 shadow-sm" // Fundo claro ao rolar
                : "bg-usifresa-dark/90 backdrop-blur-md border-b border-white/10 py-2" // Fundo escuro ao rolar
              : "bg-transparent border-b border-transparent py-4" // Topo da página: transparente
        }`}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div
            className={`flex items-center justify-between transition-all duration-500 ease-in-out ${
              scrolled || menuOpen ? "h-14 lg:h-16" : "h-16 lg:h-20"
            }`}
          >
            {/* Logo */}
            <a href="/" onClick={(e) => handleLinkClick(e, "/")} className="flex items-center relative z-[60] shrink-0">
              <img
                src="/logo-usifresa-branco.png"
                alt="Usifresa Logo"
                className={`object-contain transition-all duration-500 ease-in-out ${
                  scrolled || menuOpen ? "h-10 md:h-12" : "h-12 md:h-16"
                } ${isLight && !menuOpen ? "brightness-0" : ""}`} 
                // brightness-0 transforma a imagem branca em totalmente preta no tema light (desde que o menu mobile não esteja aberto)
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8 relative z-[60]">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path || (link.path !== "/" && location.pathname.startsWith(link.path));
                return (
                  <a 
                    key={link.name} 
                    href={link.path} 
                    onClick={(e) => handleLinkClick(e, link.path)}
                    className={`relative text-xs xl:text-sm uppercase tracking-widest font-bold transition-colors py-2 cursor-pointer ${
                      isActive 
                        ? 'text-usifresa-orange' 
                        : (isLight && !menuOpen ? 'text-usifresa-dark hover:text-usifresa-orange' : 'text-white hover:text-gray-300')
                    }`}
                  >
                    {link.name}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-usifresa-orange rounded-full"></span>
                    )}
                  </a>
                );
              })}
              <a
                href="https://loja.usifresa.com.br/"
                target="_blank"
                rel="noreferrer"
                className={`ml-4 px-6 py-2.5 rounded-md font-bold tracking-widest uppercase text-xs transition-all duration-300 ${
                  isLight && !menuOpen 
                    ? "bg-usifresa-blue text-white hover:bg-usifresa-orange" 
                    : "text-usifresa-dark bg-usifresa-light-blue hover:bg-white"
                }`}
              >
                Loja Virtual
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-4 relative z-[60]">
              <a
                href="https://loja.usifresa.com.br/"
                target="_blank"
                rel="noreferrer"
                className={`px-4 py-2 rounded-md font-bold text-xs tracking-widest uppercase ${
                  isLight && !menuOpen 
                    ? "bg-usifresa-blue text-white" 
                    : "text-usifresa-dark bg-usifresa-light-blue"
                }`}
              >
                Loja
              </a>
              <button
                className="relative w-10 h-10 flex items-center justify-center transition-all duration-300"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Menu"
              >
                {menuOpen ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 rotate-90 scale-110">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  <div className="flex flex-col gap-[6px]">
                    {/* Linhas do hambúrguer mudam de cor no tema claro */}
                    <span className={`block w-6 h-[2px] transition-colors duration-300 ${isLight ? 'bg-usifresa-dark' : 'bg-white'}`} />
                    <span className={`block w-6 h-[2px] transition-colors duration-300 ${isLight ? 'bg-usifresa-dark' : 'bg-white'}`} />
                    <span className={`block w-6 h-[2px] transition-colors duration-300 ${isLight ? 'bg-usifresa-dark' : 'bg-white'}`} />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Backdrop */}
      <div 
        className={`fixed inset-0 z-[40] bg-usifresa-dark/60 backdrop-blur-sm transition-opacity duration-500 lg:hidden ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed top-0 left-0 right-0 h-auto z-[45] lg:hidden bg-usifresa-blue rounded-b-[3.5rem] shadow-2xl overflow-hidden transition-all duration-500 ease-in-out ${
          menuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-full"
        }`}
      >
        <div className="h-16 lg:h-20 bg-usifresa-dark" />
        
        <div className="flex flex-col pb-10 pt-4 px-6">
          <nav className="flex flex-col w-full">
            {navLinks.map((link, i) => (
              <a
                key={link.name}
                href={link.path}
                onClick={(e) => handleLinkClick(e, link.path)}
                className="text-white text-center text-lg font-bold uppercase tracking-[0.2em] py-4 border-b border-white/10 transition-all hover:bg-white/5 active:bg-white/10 cursor-pointer"
                style={{
                  transform: menuOpen ? "translateY(0)" : "translateY(15px)",
                  opacity: menuOpen ? 1 : 0,
                  transitionDelay: menuOpen ? `${i * 40 + 100}ms` : "0ms",
                  transitionDuration: "400ms"
                }}
              >
                {link.name}
              </a>
            ))}
          </nav>

          <div 
            className="mt-8 flex flex-col gap-3 text-sm text-gray-300 items-center text-center"
            style={{
              transform: menuOpen ? "translateY(0)" : "translateY(15px)",
              opacity: menuOpen ? 1 : 0,
              transitionDelay: `${navLinks.length * 40 + 150}ms`,
              transitionDuration: "400ms"
            }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              <span>usifresa@usifresa.com</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              <span>(16) 3987-2253</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;